/** @file puppet.setBounds: sets the render cell's bounds (CR-010), the sizes render, export and bake fit. */
import { failField } from '@zilo/pose3d-format/validate/fields/failField';
import type { CommandDef } from '../apply/commandDef';
import { readInput } from '../validate/readInput';

/** Largest bounds size in units (CR-010). */
const MAX_BOUNDS = 64;
const HINT = `bounds = [across, depth, height] in units, each > 0 and at most ${MAX_BOUNDS}; lint BOUNDS_CLIPPED suggests a fit`;

const fits = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0 && v <= MAX_BOUNDS;

/** The three sizes, or VALIDATION at the array or the offending size, the range in the hint. */
function readBounds(value: unknown, path: string): [number, number, number] {
  if (!Array.isArray(value) || value.length !== 3) return failField(path, 'array of 3 numbers', value, HINT);
  value.forEach((v: unknown, i) => { if (!fits(v)) failField(`${path}/${i}`, `finite number > 0 <= ${MAX_BOUNDS}`, v, HINT); });
  return [...(value as [number, number, number])];
}

/**
 * `puppet.setBounds { bounds }` → `{ bounds }`: replaces `puppet.bounds`, the sizes the render cell is derived from
 * (raster's `cellSizeOf`). Inverse: the previous bounds.
 */
export const puppetSetBoundsCommand: CommandDef<'puppet.setBounds'> = {
  name: 'puppet.setBounds',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['bounds']);
    return { bounds: readBounds(r.raw('bounds'), r.at('bounds')) };
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input) => {
    const prev: [number, number, number] = [...draft.puppet.bounds];
    draft.puppet.bounds = [...input.bounds];
    return { result: { bounds: [...input.bounds] }, warnings: [], inverse: [{ name: 'puppet.setBounds', input: { bounds: prev } }] };
  },
};
